import React, { useState } from 'react';
import { Mail, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { companyInfo } from '../mockData';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Message Sent!",
          description: result.message || "Thank you for contacting us. We'll get back to you soon.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 overflow-hidden bg-gradient-to-br from-black via-[#1a1a1a] to-black">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/3 w-96 h-96 bg-[#C9A55A]/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="luxury-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6">
            Get In <span className="text-[#C9A55A]">Touch</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-3 sm:mb-4 px-4">
            For Government, Ministry, and Corporate Inquiries
          </p>
          <p className="text-sm sm:text-base text-gray-400 max-w-3xl mx-auto px-4">
            A secure communication channel will be provided upon request. Initial inquiries may be submitted through the form below.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-4 sm:space-y-6">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#C9A55A]" size={20} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Location</h3>
                      <p className="text-gray-400 text-xs sm:text-sm">{companyInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#C9A55A]" size={20} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1 sm:mb-2 text-sm sm:text-base">Inquiries</h3>
                      <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                        For government and corporate inquiries, please submit the form. We respond within 24 hours.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-white/5 border border-white/10 rounded-lg p-4 sm:p-6">
                <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Response Time</h3>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-start space-x-2">
                    <span className="text-[#C9A55A] mt-0.5">•</span>
                    <span className="text-gray-300">Government inquiries: Priority response within 24 hours</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-[#C9A55A] mt-0.5">•</span>
                    <span className="text-gray-300">Corporate inquiries: Response within 48 hours</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-[#C9A55A] mt-0.5">•</span>
                    <span className="text-gray-300">All communications handled with strict confidentiality</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-6 sm:p-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C9A55A] text-sm sm:text-base"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C9A55A] text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                          Phone Number
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (555) 123-4567"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C9A55A] text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                          Company Name
                        </label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C9A55A] text-sm sm:text-base"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project or inquiry..."
                        rows={6}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#C9A55A] resize-none text-sm sm:text-base"
                        required
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-base sm:text-lg py-5 sm:py-6 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="mr-2" size={18} />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Global Reach Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h2 className="luxury-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-6">Our Global Reach</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              Operating across four continents with strategic presence in key markets
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 text-center">
              <CardContent className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-[#C9A55A]/20">
                  <MapPin className="text-[#C9A55A]" size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">Canada</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Vancouver Headquarters</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 text-center">
              <CardContent className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-[#C9A55A]/20">
                  <MapPin className="text-[#C9A55A]" size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">Middle East</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Government Relations</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 text-center">
              <CardContent className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-[#C9A55A]/20">
                  <MapPin className="text-[#C9A55A]" size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">Europe</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Milan & Paris Sourcing</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 text-center">
              <CardContent className="p-6 sm:p-8">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#C9A55A]/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 border border-[#C9A55A]/20">
                  <MapPin className="text-[#C9A55A]" size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2">United States</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Strategic Partnerships</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;